import { Key } from "@ms/uno-constants/lib/local/KeyCode";
// Controls
import { IconButton } from "./../../iconButton/IconButton";
import { SearchBox, css, useTheme } from "@fluentui/react";
// Styles
import { getIconButtonStyles, getSearchBoxStyles } from "./KeywordFilter.styles";
import { searchIconProps } from "./../../../icons/icons.styles";
// Utilities
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
/**
 * The keyword filter component.
 * Accepts user input to filter the existing list of plans.
 */ export function KeywordFilter({ placeholder, filterInput, onFilterInputUpdated }) {
    // State
    const [expandFilterBox, setExpandFilterBox] = useState(filterInput.trim().length > 0);
    const [filterBoxClassName, setFilterBoxClassName] = useState(filterInput.trim().length > 0 ? "expand" : "collapse");
    // State for the currently typed text in the filter box.
    // Local state is necessary to avoid Japanese IME input issues.
    const [currentlyTypedText, setCurrentlyTypedText] = React.useState(filterInput);
    // Refs
    const inputRef = useRef(null);
    const buttonRef = useRef(null);
    const theme = useTheme();
    // Keep the internal state in sync with the external filter input
    useEffect(()=>{
        setCurrentlyTypedText(filterInput);
    }, [
        filterInput
    ]);
    useEffect(()=>{
        if (expandFilterBox) {
            inputRef.current?.focus();
            /* Need to ensure SearchBox has initial state at render followed by another state to transition to.
            https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Transitions/Using_CSS_transitions#javascript_examples */ setTimeout(()=>{
                setFilterBoxClassName("expand");
            }, 0);
        }
    }, [
        expandFilterBox
    ]);
    // Handle Ctrl + F6 keyboard shortcut to focus SearchBox
    useEffect(()=>{
        const handleKeyDown = (event)=>{
            if (event.ctrlKey && event.key === Key.F6) {
                event.preventDefault();
                if (expandFilterBox) {
                    inputRef.current?.focus();
                } else {
                    buttonRef.current?.focus();
                }
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return ()=>{
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [
        expandFilterBox
    ]);
    //Auto-expand filter box when input is not empty
    useEffect(()=>{
        if (filterInput.length > 0 && !expandFilterBox) {
            setExpandFilterBox(true);
        }
    }, [
        filterInput,
        expandFilterBox
    ]);
    /** Callback for when keyword filter button is clicked */ const onSearchBoxBlur = React.useCallback((event)=>{
        // Collapse the filter box only when input is empty
        if (filterInput.length === 0) {
            // If the related target is a pivot link, don't collapse the filter box
            const eventTarget = event.relatedTarget;
            // TODO: Bug 8778119 - Use more robust way to check if the related target is a pivot link or better solution
            if (eventTarget?.className.includes("ms-Pivot-link")) {
                return;
            }
            /* Need to ensure SearchBox receives classname update before state update causes filter removal from DOM.
                https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Transitions/Using_CSS_transitions#javascript_examples */ setTimeout(()=>{
                setExpandFilterBox(false);
            }, 200);
            setFilterBoxClassName("collapse");
        }
    }, [
        filterInput
    ]);
    /** onChange callback to update filterInput value (state) */ const onSearchBoxChange = useCallback((event, newValue)=>{
        const newFilterValue = newValue ?? "";
        onFilterInputUpdated(newFilterValue);
        setCurrentlyTypedText(newFilterValue);
    }, [
        onFilterInputUpdated
    ]);
    /** onClear callback to clear the input's value */ const onSearchBoxClear = useCallback(()=>{
        onFilterInputUpdated("");
        // Return focus to filter box to prevent collapse after input is cleared
        inputRef.current?.focus();
    }, [
        onFilterInputUpdated
    ]);
    /**onClick callback to expand the keyword filter */ const onClickButton = useCallback(()=>{
        setExpandFilterBox(true);
    }, []);
    return !expandFilterBox ? /*#__PURE__*/ React.createElement(IconButton, {
        componentRef: buttonRef,
        ariaLabel: placeholder,
        title: placeholder,
        className: "keyword-filter-button",
        iconProps: searchIconProps,
        onClick: onClickButton,
        styles: getIconButtonStyles(theme)
    }) : /*#__PURE__*/ React.createElement(SearchBox, {
        componentRef: inputRef,
        className: css("keyword-filter-box", filterBoxClassName),
        onBlur: (ev)=>onSearchBoxBlur(ev),
        value: currentlyTypedText,
        onChange: onSearchBoxChange,
        onClear: onSearchBoxClear,
        clearButtonProps: {
            title: Strings.Tooltip_ClearButtonTooltipText
        },
        underlined: true,
        iconProps: {
            ...searchIconProps
        },
        placeholder: placeholder,
        styles: getSearchBoxStyles()
    });
}
